import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { authService, projectService } from "@/lib/supabase";
import type { CreateProjectDTO } from "@palman/api";
import { useUser } from "@/contexts/UserProvider";
import { Dialog } from "@/components/ui/dialog";
import { toast } from "sonner";
import { overlay } from "overlay-kit";
import { ArrowLeft, Loader2, LogOut } from "lucide-react";
import { ProjectCreateDialogContent } from "@/components/project/project-create-dialog-content";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Trans, useTranslation } from "react-i18next";

export const Route = createFileRoute("/_authenticated/project-create-or-join")({
  component: RouteComponent,
});

type ProjectFormData = {
  projectName: string;
};

function RouteComponent() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user, refreshUser } = useUser();
  const { queryClient } = Route.useRouteContext();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<ProjectFormData>();

  const { mutateAsync: switchCurrentProject } = useMutation({
    mutationFn: ({ projectId }: { projectId: string }) =>
      projectService.switchCurrentProject(projectId, user.id),
  });

  const { mutateAsync: createProject } = useMutation({
    mutationFn: ({ userId, ...data }: { userId: string } & CreateProjectDTO) =>
      projectService.createProject(data, userId),
    onSuccess: () => {
      toast.success(
        <Trans i18nKey={"projectCreateOrJoin.projectCreate.success"}>
          Project created
        </Trans>,
      );
    },
  });

  const {
    mutateAsync: acceptInvitationByToken,
    isPending,
    variables,
  } = useMutation({
    mutationFn: ({ token }: { token: string }) =>
      projectService.acceptInvitationByToken(token, user.id),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["projectService", "getUserInvitations"],
    queryFn: () => projectService.getUserInvitations(user.email, "pending"),
  });
  const isInvitedProjectExist = data && data.length > 0;

  const { data: userProjectsData } = useQuery({
    queryKey: ["projectService", "getUserProjects"],
    queryFn: () => projectService.getUserProjects(user.id),
  });

  const userProjectId =
    user.currentProjectId || (userProjectsData && userProjectsData[0]?.id);

  const onSubmit = async (data: ProjectFormData) => {
    try {
      const { id: projectId } = await createProject({
        userId: user.id,
        name: data.projectName,
      });
      await switchCurrentProject(
        { projectId },
        {
          onSuccess: async () => {
            queryClient.invalidateQueries();
            await refreshUser();
            router.navigate({ to: "/$projectId", params: { projectId } });
          },
        },
      );
    } catch {
      setError("root", {
        type: "manual",
        message: t("projectCreateOrJoin.projectCreate.submitError", {
          defaultValue: "An unexpected error occurred",
        }),
      });
    }
  };

  const handleJoin = async ({ token }: { token: string }) => {
    try {
      const { id: projectId } = await acceptInvitationByToken({
        token,
      });
      await switchCurrentProject(
        { projectId },
        {
          onSuccess: async () => {
            queryClient.invalidateQueries();
            await refreshUser();
            router.navigate({ to: "/$projectId", params: { projectId } });
          },
        },
      );
    } catch {
      setError("root", {
        type: "manual",
        message: t("projectCreateOrJoin.projectJoin.error", {
          defaultValue: "An unexpected error occurred",
        }),
      });
    }
  };

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        {isLoading ? (
          <Loader2 className="animate-spin" />
        ) : isInvitedProjectExist ? (
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey={"projectCreateOrJoin.projectJoin.title"}>
                  Join a project
                </Trans>
              </CardTitle>
              <CardDescription>
                <Trans i18nKey={"projectCreateOrJoin.projectJoin.description"}>
                  Accept an invitation to a project
                </Trans>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                {(data || []).map(({ project, token }, i) => (
                  <div key={project.id}>
                    {i > 0 ? <Separator /> : null}
                    <div className="flex items-center gap-x-2 justify-between py-2">
                      <span className="text-base">{project.name}</span>
                      <Button
                        size="sm"
                        onClick={() => handleJoin({ token })}
                        disabled={isPending}
                      >
                        {variables?.token === token && isPending ? (
                          <Trans
                            i18nKey={
                              "projectCreateOrJoin.projectJoin.joinLoading"
                            }
                          >
                            Joining...
                          </Trans>
                        ) : (
                          <Trans
                            i18nKey={"projectCreateOrJoin.projectJoin.join"}
                          >
                            Join
                          </Trans>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              {/* OR Divider */}
              <div className="relative">
                <Separator />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-background px-2 text-sm text-muted-foreground">
                    <Trans i18nKey={"projectCreateOrJoin.OR"}>OR</Trans>
                  </span>
                </div>
              </div>

              <Button
                className="w-full"
                variant="secondary"
                onClick={() =>
                  overlay.open(({ isOpen, close }) => (
                    <Dialog open={isOpen} onOpenChange={close}>
                      <ProjectCreateDialogContent />
                    </Dialog>
                  ))
                }
              >
                <Trans
                  i18nKey={"projectJoinOrCreate.projectJoin.createNewProject"}
                >
                  Create new project
                </Trans>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>
                <Trans i18nKey={"projectCreateOrJoin.projectCreate.title"}>
                  Create a project
                </Trans>
              </CardTitle>
              <CardDescription>
                <Trans
                  i18nKey={"projectCreateOrJoin.projectCreate.description"}
                >
                  Create a new project to start collaborating
                </Trans>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="flex flex-col gap-6">
                  <div className="grid gap-3">
                    <Label htmlFor="projectName">
                      <Trans
                        i18nKey={
                          "projectCreateOrJoin.projectCreate.projectNameLabel"
                        }
                      >
                        Project Name
                      </Trans>
                    </Label>
                    <Input
                      id="projectName"
                      type="text"
                      placeholder={t(
                        "projectCreateOrJoin.projectCreate.projectNamePlaceholder",
                        {
                          defaultValue: "My Awesome Project",
                        },
                      )}
                      {...register("projectName", {
                        required: t(
                          "projectCreateOrJoin.projectCreate.projectNameRequired",
                          {
                            defaultValue: "Project name is required",
                          },
                        ),
                        minLength: {
                          value: 3,
                          message: t(
                            "projectCreateOrJoin.projectCreate.projectNameMinLength",
                            {
                              defaultValue:
                                "Project name must be at least 3 characters",
                            },
                          ),
                        },
                      })}
                    />
                    {errors.projectName && (
                      <p className="text-sm text-red-600">
                        {errors.projectName.message}
                      </p>
                    )}
                  </div>

                  {errors.root && (
                    <p className="text-sm text-red-600">
                      {errors.root.message}
                    </p>
                  )}

                  <div className="flex flex-col gap-3">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Trans
                          i18nKey={"projectCreateOrJoin.projectCreate.creating"}
                        >
                          Creating...
                        </Trans>
                      ) : (
                        <Trans
                          i18nKey={
                            "projectCreateOrJoin.projectCreate.createProject"
                          }
                        >
                          Create Project
                        </Trans>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
      {userProjectId ? (
        <div className="fixed top-6 left-5">
          <Button
            variant="link"
            onClick={() => {
              if (user.currentProjectId) {
                router.navigate({
                  to: "/$projectId",
                  params: { projectId: userProjectId },
                });
              } else {
                switchCurrentProject(
                  { projectId: userProjectId },
                  {
                    onSuccess: async () => {
                      await queryClient.invalidateQueries();
                      await refreshUser();
                      await router.navigate({
                        to: "/$projectId",
                        params: { projectId: userProjectId },
                      });
                    },
                  },
                );
              }
            }}
          >
            <ArrowLeft />{" "}
            <Trans i18nKey={"projectCreateOrJoin.goBackToCurrentProject"}>
              Go back to current project
            </Trans>
          </Button>
        </div>
      ) : null}
      <div className="fixed top-6 right-5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost">{user.email}</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
            <DropdownMenuItem onClick={() => authService.signOut()}>
              <LogOut />
              <Trans i18nKey={"projectCreateOrJoin.logOut"}>Log out</Trans>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
